import { apiCacheKeys } from '@doltech/core/lib/api/api.urls';
import { getUserAnonymousId } from '@doltech/core/lib/auth/constants';
import { fromScreen, toScreen } from '@doltech/core/lib/responsive/responsive.util';
import { IconButtonsSoftwareUpload } from '@doltech/icons/IconButtonsSoftwareUpload';

import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { CardNews } from '@doltech/ui/lib/figma/DailyLearning/UserView/CardNews';
import { CardVocabDetail } from '@doltech/ui/lib/figma/DailyLearning/UserView/CardVocabDetail';
import { HeroListing } from '@doltech/ui/lib/figma/DailyLearning/UserView/HeroListing';
import { PostListing } from '@doltech/ui/lib/figma/DailyLearning/UserView/PostListing';
import { RecentNewsPostListing } from '@doltech/ui/lib/figma/DailyLearning/ui/RecentNewsPostListing/RecentNewsPostListing';

import { RiddleCard } from '@doltech/ui/lib/figma/DailyLearning/UserView/RiddleCard';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { DolStory } from '@doltech/ui/lib/figma/DolStory/DolStory';
import { Separator } from '@doltech/ui/lib/figma/Layout/Landing/Content/Separator';
import { DailyLearningFooter } from '@doltech/ui/lib/figma/Layout/Landing/Footer/DailyLearningFooter/DailyLearningFooter';
import { LandingLayoutDailyLearning } from '@doltech/ui/lib/figma/Layout/Landing/PremadeLayout/LandingLayoutDailyLearning';
import { PromotionBarShareBanner } from '@doltech/ui/lib/figma/Layout/Landing/ui/PromotionBarShareBanner/PromotionBarShareBanner';
import { SectionHero } from '@doltech/ui/lib/figma/Layout/Landing/ui/SectionHero/SectionHero';
import { Skeleton } from '@doltech/ui/lib/figma/Skeleton/Skeleton';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { withWrapper } from '@doltech/ui/lib/hocs/withWrapper';
import { withAudioPlayInBackground } from '@doltech/ui/lib/hooks/audio-play-in-background.store';
import { compose } from '@doltech/utils/lib/compose';
import { Spin } from 'antd';
import cl from 'classnames';
import { take } from 'lodash';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { useQueries, useQuery } from 'react-query';
import { Node } from 'slate';
import styled, { css } from 'styled-components';

import { PreviewDailyNewsMain } from '../../../ielts-sample/sample.utils';
import {
  getActiveFact,
  getActiveRiddle,
  getActiveStories,
  getActiveVocab,
  getLatestNewsList,
  getPopularNewsList,
  getRecentNewsList,
} from '../../apis/daily-learning-news.api';
import { getRiddleProgress } from '../../apis/daily-learning-riddles.api';
import { FactCard } from '../../data-entry/Facts/FactCard';
import { getInitialBody } from '../../data-entry/News/daily-news.utils';
import {
  useRecentNewsFilterStore,
  withRecentNewsFilterStore,
} from './components/RecentNewsFilterConfig';
import { TitleH2DailyLearning } from './components/TitleH2DailyLearning';
import { FactSelectionModal } from './Modal/FactSelectionModal';
import { RiddleSelectionModal } from './Modal/RiddleSelectionModal';
import { VocabSelectionModal } from './Modal/VocabSelectionModal';
import { FABLandingHome } from '../../../landing-home/components/FABLandingHome';
import { ContactDol } from '@doltech/ui/lib/figma/ContactDol/ContactDol';

const ButtonRecentFilter = dynamic<any>(
  () =>
    import('./components/RecentNewFilter/ButtonRecentFilter').then((mod) => mod.ButtonRecentFilter),
  { loading: () => <div />, ssr: false }
);

const RecentFilterValues = dynamic(
  () =>
    import('./components/RecentNewFilter/ButtonRecentFilter').then((mod) => mod.RecentFilterValues),
  { loading: () => <div />, ssr: false }
);

export const gridSpacing = css`
  display: grid;
  gap: 32px;

  ${fromScreen(768)} {
    gap: 44px;
  }
`;

/* stylelint-disable no-descending-specificity */
const Main = styled(PreviewDailyNewsMain)`
  .wrapper {
    padding: 32px 16px;
    ${fromScreen(768)} {
      padding: 44px 16px;
    }
  }

  background-color: transparent;

  .hide-below-desktop {
    display: none;
    ${fromScreen(1024)} {
      display: grid;
    }
  }

  .hide-on-desktop {
    ${fromScreen(1024)} {
      display: none;
    }
  }

  .vertical-separator {
    display: none;
    ${fromScreen(1024)} {
      display: block;
    }
  }

  .section-container {
    margin: 0 auto;
    ${gridSpacing};
  }

  .section-block {
    gap: 24px;
  }

  .main-content {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;

    .right-sidebar {
      display: grid;
      grid-auto-rows: max-content;
      gap: 64px;
      .daily-learning-footer {
        padding-top: 32px;
      }
    }

    .left-content {
      ${gridSpacing};
      grid-auto-rows: max-content;

      grid-template-columns: minmax(0, 1fr);
      #tu-vung-hom-nay {
        order: 1;
      }
      #pho-bien {
        order: 2;
      }
      #gan-day {
        display: none;
      }
    }
    ${fromScreen(768)} {
      gap: 44px;

      .wrapper {
        padding: 44px 16px;
      }
      .right-sidebar {
        gap: 80px;
      }
    }
    ${fromScreen(1024)} {
      grid-template-columns: minmax(0, 1fr) 416px;
      gap: 32px;

      .right-sidebar {
        gap: 64px;
      }
      .left-content {
        #tu-vung-hom-nay {
          order: 3;
        }
        #pho-bien {
          order: 2;
        }
        #gan-day {
          order: 4;
          grid-auto-rows: max-content;
          display: grid;
        }
      }
    }
  }

  .popular-news-post-listing,
  .recent-news-post-listing {
    display: grid;
    gap: 32px;

    .card-news {
      display: grid;
      grid-template-columns: 100px minmax(0, 1fr);
      gap: 12px;
      .card-news-cover {
        height: 100px;
        margin-bottom: 0;
      }

      ${fromScreen(768)} {
        grid-template-columns: 163px minmax(0, 1fr);
        .card-news-cover {
          height: 108px;
        }
      }
    }
  }

  #story-mobile,
  #story-desktop {
    width: 100%;
  }

  #story-desktop {
    display: none;
    ${fromScreen(1024)} {
      display: block;
    }
  }
  #story-mobile {
    display: block;
    ${fromScreen(1024)} {
      display: none;
    }
  }

  ${toScreen(1023)} {
    .header-info {
      max-width: 528px;
    }
  }
`;

interface CardMarketerEditProps {
  title: string;
  isEmpty?: boolean;
  onClick: () => void;
}

const CardMarketerEditMain = styled.div`
  cursor: pointer;
  .empty-wrapper {
    display: grid;
    place-items: center;
    gap: 16px;
    text-align: center;
    padding: 18px 20px;

    background: ${colorsV2.gray20};
    border: 1px dashed ${colorsV2.black40};
    box-sizing: border-box;
    border-radius: 12px;
    .icon-upload-wrapper {
      width: 72px;
      height: 72px;
      min-width: 72px;
      min-height: 72px;
      border-radius: 50%;
      color: ${colorsV2.white100};
      background-color: ${colorsV2.gray40};
      font-size: 24px;
      line-height: 0;
      display: grid;
      place-items: center;
    }
  }
`;

const CardMarketerEdit = (props: CardMarketerEditProps) => {
  const { title, isEmpty, onClick } = props;
  return (
    <CardMarketerEditMain onClick={onClick}>
      {isEmpty ? (
        <div className="empty-wrapper">
          <div aria-hidden className="icon-upload-wrapper">
            <IconButtonsSoftwareUpload />
          </div>
          <Typography.Paragraph variant="semi-bold/16">{title}</Typography.Paragraph>
        </div>
      ) : (
        <Button.Secondary fullWidth size="large">
          Upload
        </Button.Secondary>
      )}
    </CardMarketerEditMain>
  );
};

const LandingDailyLearningPreviewBase = observer((props: any) => {
  const {
    seoData,
    latestNews,
    popularNews,
    mainNews,
    // recentNews,
    activeVocab,
    activeRiddle,
    activeFact,
    activeStories,

    excludedPopularNewsIds = [],

    withMarketerEdit = false,
  } = props;

  const [isSelectFactModalVisible, setSelectFactModalVisible] = React.useState(false);
  const [isSelectVocabModalVisible, setSelectVocabModalVisible] = React.useState(false);
  const [isSelectRiddleModalVisible, setSelectRiddleModalVisible] = React.useState(false);
  const recentFilterStore = useRecentNewsFilterStore();

  const filterObj = recentFilterStore.filterKeys.reduce((prevValue, currentValue) => {
    return {
      ...prevValue,
      [currentValue.key]: currentValue.value,
    };
  }, {});

  const riddleProgressQuery = useQuery(
    ['GET_DAILY_LEARNING_RIDDLE_PROGRESS', activeRiddle?.id, getUserAnonymousId()],
    () => {
      return getRiddleProgress(activeRiddle?.id);
    },
    {
      enabled: Boolean(activeRiddle?.id),
    }
  );

  const renderStory = React.useCallback(
    (id) => {
      if (activeStories?.length > 0) {
        return (
          <div id={id} className="section-block">
            <DolStory
              data={
                activeStories.map((story) => {
                  const { content, translatedContent, image } = story;
                  return {
                    id: story.id,
                    title: content,
                    image,
                    description: translatedContent,
                  };
                }) || []
              }
            />
          </div>
        );
      }
      return null;
    },
    [activeStories]
  );

  const renderFact = React.useCallback(() => {
    return (
      <div id="su-that-ban-chua-biet" className="section-block">
        {activeFact && (
          <FactCard
            colorString={activeFact?.background}
            text={activeFact?.content}
            imageUrl={activeFact?.image?.url}
          />
        )}
        {withMarketerEdit && (
          <CardMarketerEdit
            title="Pickup the fact"
            isEmpty={Boolean(activeFact) === false}
            onClick={() => {
              setSelectFactModalVisible(true);
            }}
          />
        )}
      </div>
    );
  }, [activeFact, withMarketerEdit]);

  const renderRiddle = React.useCallback(() => {
    return (
      <div id="cau-do-hom-nay" className="section-block">
        {activeRiddle && !riddleProgressQuery.isLoading && (
          <RiddleCard data={{ ...activeRiddle, ...((riddleProgressQuery.data as any) || {}) }} />
        )}
        {withMarketerEdit && (
          <CardMarketerEdit
            title="Pickup the riddle"
            isEmpty={Boolean(activeRiddle) === false}
            onClick={() => {
              setSelectRiddleModalVisible(true);
            }}
          />
        )}
      </div>
    );
  }, [activeRiddle, riddleProgressQuery.data, riddleProgressQuery.isLoading, withMarketerEdit]);

  const renderRecentNews = React.useCallback(
    (id, className = '') => {
      const listQueryKey = [
        apiCacheKeys.publicService.GET_RECENT_NEWS_LIST,
        ...excludedPopularNewsIds,
        JSON.stringify(filterObj),
      ];
      return (
        <div id={id} className={cl('section-block', className)}>
          <TitleH2DailyLearning renderRight={<ButtonRecentFilter />} className="font-inter">
            Gần đây
          </TitleH2DailyLearning>
          <RecentFilterValues />
          <RecentNewsPostListing
            className="recent-news-post-listing"
            apiKey={listQueryKey}
            apiFn={(params) => {
              return getRecentNewsList({
                ...params,
                excludedIds: excludedPopularNewsIds,
                ...filterObj,
              });
            }}
            pageSize={10}
            infiniteScrollEnabled
            renderItem={(item) => <CardNews key={item?.id} {...item} description={null} />}
            renderSkeleton={() => <Skeleton.Card directional="horizontal" />}
          />
        </div>
      );
    },
    [excludedPopularNewsIds, filterObj]
  );

  const renderVocab = React.useCallback(() => {
    if (!withMarketerEdit && !activeVocab) return null;

    return (
      <div id="tu-vung-hom-nay" className="section-block">
        {activeVocab && (
          <CardVocabDetail
            word={activeVocab.word}
            partOfSpeech={activeVocab.partOfSpeech}
            pronunciation={activeVocab.pronunciation}
            definition={activeVocab.definition}
            example={activeVocab.example}
          />
        )}
        {withMarketerEdit && (
          <CardMarketerEdit
            title="Pickup the vocab"
            isEmpty={Boolean(activeVocab) === false}
            onClick={() => {
              setSelectVocabModalVisible(true);
            }}
          />
        )}
      </div>
    );
  }, [activeVocab, withMarketerEdit]);

  return (
    <Main>
      <ContactDol />
      <FABLandingHome />
      <PromotionBarShareBanner />
      <SectionHero title={seoData?.dol?.title} description={seoData?.dol?.description} />
      <div className="wrapper">
        <div className="section-container">
          {renderStory('story-mobile')}
          <div id="moi-nhat" className="section-block">
            <ResponsiveTypography.Title
              as="h2"
              variant={['bold/24-32', 'bold/32-40']}
              breakpoints={[0, 768]}
              color="black100"
            >
              Mới nhất
            </ResponsiveTypography.Title>
            <HeroListing
              className="latest-news-hero-listing"
              dataSource={latestNews}
              renderItem={(item) => <CardNews key={item?.id} {...item} description={null} />}
              renderMainItem={(item) => {
                const body = getInitialBody(mainNews);
                const content = (body?.content || [])
                  .filter((n) => ['h2', 'h3'].includes(n.type) === false)
                  .filter((n) => {
                    const nodeText = Node.string(n);
                    return nodeText.trim().length !== 0;
                  });
                const bodyText = take(content, 3).reduce((p: any, n: any) => {
                  return `${p}${Node.string(n)}\n`;
                }, '');
                return <CardNews key={item?.id} {...item} description={bodyText} />;
              }}
            />
          </div>
          <Separator variant="horizontal" bg="gray20" height="1px" />

          <div className="main-content">
            <div className="left-content">
              {renderStory('story-desktop')}
              <div id="pho-bien" className="section-block">
                <TitleH2DailyLearning className="font-inter">Phổ biến</TitleH2DailyLearning>
                <PostListing
                  className="popular-news-post-listing"
                  apiKey={[
                    `${apiCacheKeys.publicService.GET_POPULAR_NEWS_LIST}_landing`,
                    ...excludedPopularNewsIds,
                  ]}
                  apiFn={() => getPopularNewsList('TOP_5_LATEST')}
                  pageSize={5}
                  ssrData={popularNews}
                  infiniteScrollEnabled={false}
                  renderItem={(item) => <CardNews key={item?.id} {...item} description={null} />}
                  renderSkeleton={() => <Skeleton.Card directional="horizontal" rtl />}
                />
              </div>
              {renderRecentNews('gan-day')}
            </div>

            <div className="right-sidebar">
              {renderFact()}
              {renderRiddle()}
              {renderVocab()}
              <div id="footer" className="hide-below-desktop">
                <Separator variant="horizontal" bg="gray20" height="1px" />
                <DailyLearningFooter />
              </div>
            </div>
            <Separator variant="horizontal" bg="gray20" height="1px" />

            {renderRecentNews('gan-day-mobile', 'hide-on-desktop')}
          </div>
        </div>
      </div>

      {withMarketerEdit && (
        <>
          <FactSelectionModal
            visible={isSelectFactModalVisible}
            onCancel={() => setSelectFactModalVisible(false)}
          />
          <RiddleSelectionModal
            visible={isSelectRiddleModalVisible}
            onCancel={() => setSelectRiddleModalVisible(false)}
          />
          <VocabSelectionModal
            visible={isSelectVocabModalVisible}
            onCancel={() => setSelectVocabModalVisible(false)}
          />
        </>
      )}
    </Main>
  );
});

export const LandingDailyLearningPreview = compose(
  withAudioPlayInBackground,
  withWrapper(LandingLayoutDailyLearning),
  withRecentNewsFilterStore
)(LandingDailyLearningPreviewBase);

const MarketereLandingDailyLearningPreviewMain = styled.div`
  .landing-header {
    z-index: 10;
  }
  .fake-header {
    display: none;
  }
`;

export const MarketereLandingDailyLearningPreview = (props) => {
  const { seoData } = props;
  const [queryNewsList, queryActiveRiddle, queryActiveVocab, queryActiveFact, queryActiveStories] =
    useQueries([
      {
        queryKey: [apiCacheKeys.publicService.GET_POPULAR_NEWS_LIST],
        queryFn: async () => {
          const latestNewsList = await getLatestNewsList('TOP_5_LATEST');
          const excludedLatestNewsIds = latestNewsList.content.map((item) => item.id);
          const popularNewsList = await getPopularNewsList({ excludedIds: excludedLatestNewsIds });
          const excludedPopularNewsIds = [
            ...excludedLatestNewsIds,
            (await popularNewsList).content.map((item) => item.id),
          ];
          const recentNewsList = await getRecentNewsList({
            page: 0,
            size: 5,
            excludedIds: excludedPopularNewsIds,
          });

          return {
            latestNewsList,
            popularNewsList,
            recentNewsList,
            excludedPopularNewsIds,
          };
        },
        refetchOnMount: true,
        retry: false,
      },
      {
        queryKey: [apiCacheKeys.publicService.GET_ACTIVE_RIDDLE],
        queryFn: async () => {
          return getActiveRiddle();
        },
        refetchOnMount: true,
        retry: false,
      },
      {
        queryKey: [apiCacheKeys.publicService.GET_ACTIVE_VOCAB],
        queryFn: async () => {
          return getActiveVocab();
        },
        refetchOnMount: true,
        retry: false,
      },
      {
        queryKey: [apiCacheKeys.publicService.GET_ACTIVE_FACT],
        queryFn: async () => {
          return getActiveFact();
        },
        refetchOnMount: true,
        retry: false,
      },
      {
        queryKey: [apiCacheKeys.publicService.GET_ACTIVE_STORIES],
        queryFn: async () => {
          return getActiveStories();
        },
        refetchOnMount: true,
        retry: false,
      },
    ]);

  const isLoading =
    queryNewsList.isLoading ||
    queryActiveRiddle.isLoading ||
    queryActiveVocab.isLoading ||
    queryActiveFact.isLoading ||
    queryActiveStories.isLoading;

  return (
    <Spin spinning={isLoading}>
      <MarketereLandingDailyLearningPreviewMain>
        <LandingDailyLearningPreview
          latestNews={(queryNewsList.data as any)?.latestNewsList?.content || []}
          popularNews={(queryNewsList.data as any)?.popularNewsList || []}
          recentNews={(queryNewsList.data as any)?.recentNewsList || []}
          excludedPopularNewsIds={(queryNewsList.data as any)?.excludedPopularNewsIds || []}
          activeVocab={queryActiveVocab.data}
          activeRiddle={queryActiveRiddle.data}
          activeFact={queryActiveFact.data}
          activeStories={(queryActiveStories.data as any)?.content || []}
          seoData={seoData}
          withMarketerEdit
        />
      </MarketereLandingDailyLearningPreviewMain>
    </Spin>
  );
};
